import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { Loader } from "components/loader/loader";
import { RenderHTML } from "components/sanitizeHtml/sanitizeHtml";
import { LearningRequestWorkflowInput, SurveyType } from "utils/graphql/Global";
import portalSettingsManager from "utils/portalSettingsManager";
import { START_LEARNING_REQUEST_FLOW, SURVEY_DATA } from "utils/queries";
import { logger } from "utils/logger";

import { SurveyContainer } from "../../question-types/survey-container/survey-container";
import { useAppDispatch } from "store";
import { mapToResponse, validateAllQuestions } from "../../shared/validation";
import { useLearningAnalysisStyles } from "./style";
import * as SurveyTypes from "utils/graphql/SurveyData";
import { NewLearningRequestStep, WorkflowStageEnum } from "utils/types";
import {
  StartLearningRequestWorkflow,
  StartLearningRequestWorkflowVariables,
} from "utils/graphql/StartLearningRequestWorkflow";
import { setWorkflowStage } from "store/newLearningRequest";
import {
  focusFirstError,
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  showAction,
} from "../../shared/newLearningHelper";

interface LearningAnalysisProps {
  onPrevious: (currentStepParam: NewLearningRequestStep) => void;
  isMobile: boolean;
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  setDialogTitle?: (title: string) => void;
}

export const LearningAnalysis: React.FC<LearningAnalysisProps> = ({
  onPrevious,
  isMobile,
  onNext,
  setDialogTitle,
}) => {
  const classes = useLearningAnalysisStyles({ isMobile });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [questions, setQuestions] = useState<SurveyTypes.SurveyData_survey_questions[]>([]);
  const [surveyId, setSurveyId] = useState<string>("");
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, any>>({});
  const { loading, data, error } = useQuery<SurveyTypes.SurveyData>(SURVEY_DATA, {
    variables: {
      surveyType: SurveyType.NLR,
    },
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (error) {
      logger.error("Failed to load your learning requirements data", { error: error.message });

      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("learning_requirements"),
        message: t("learning_requirements_step_load_error_alert_message"),
      });

      setQuestions([]);
    }
  }, [error, dispatch, t]);

  const [StartLearningRequestFlow] = useMutation<
    StartLearningRequestWorkflow,
    StartLearningRequestWorkflowVariables
  >(START_LEARNING_REQUEST_FLOW);

  // Refs for focus management - stores DOM references to each question container
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (data) {
      setQuestions(data.survey.questions);
      setSurveyId(data.survey.surveyId);
    }
  }, [data]);

  useEffect(() => {
    setDialogTitle?.(t("learning_requirements"));
  }, [setDialogTitle, t]);

  const maxCharacters =
    portalSettingsManager.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMaxLength;

  const handleAnswerChange = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [questionId]: {},
    }));
  }, []);

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const learnerAnswers = mapToResponse(answers);

    const learningRequestWorkflowInput: LearningRequestWorkflowInput = {
      knowsCourse: false,
      surveyId,
      learnerAnswers,
    };

    logger.info("Submitting your learning requirements", {
      surveyId,
      answerCount: learnerAnswers?.length ?? 0,
    });

    try {
      const { data } = await StartLearningRequestFlow({
        variables: {
          payload: learningRequestWorkflowInput,
        },
      });
      const stage = (data?.startLearningRequestWorkflow?.workflowState?.workflowStage ??
        null) as WorkflowStageEnum | null;
      dispatch(setWorkflowStage(stage));

      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.SUCCESS,
        title: t("learning_requirements"),
        message: t("learning_requirements_step_submit_success_alert_message"),
      });
      return true;
    } catch (error) {
      dispatch(setWorkflowStage(WorkflowStageEnum.Failed));
      logger.error("Failed to submit your learning requirements", {
        surveyId,
        error: error instanceof Error ? error.message : String(error),
      });

      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("learning_requirements"),
        message: t("learning_requirements_step_submit_error_alert_message"),
      });
      return false;
    }
  }, [answers, surveyId, StartLearningRequestFlow, dispatch, t]);

  const handleNext = useCallback(async () => {
    if (!questions.length) return;

    const { isValid, errors } = validateAllQuestions(questions, answers, {
      skipMinCharValidation: true,
    });

    if (!isValid) {
      setValidationErrors(errors);

      // This helps keyboard and screen reader users immediately know where the problem is
      focusFirstError(errors, questionRefs.current);
      return;
    }

    const success = await handleSubmit();
    if (success) {
      onNext(NewLearningRequestStep.Recommendations);
    }
  }, [questions, answers, handleSubmit]);

  const handleBackClick = () => {
    setAnswers({});
    setValidationErrors({});
    onPrevious(NewLearningRequestStep.NewLearning);
  };

  return (
    <Box className={classes.container}>
      <Loader isDisplay={loading}></Loader>
      <RenderHTML className={classes.headerDescription} content={t("tna_header")} />

      <Box className={classes.contentCard}>
        <Typography variant="h2" className={classes.cardTitle}>
          {t("learning_requirements")}{" "}
        </Typography>
        <Box className={classes.fieldsContainer}>
          {questions?.map((question, index) => {
            return (
              <div
                key={question.questionId}
                ref={(el) => {
                  questionRefs.current[question.questionId] = el;
                }}
              >
                <Box className={classes.questionItem}>
                  <SurveyContainer
                    question={question}
                    questionIndex={index}
                    answer={answers[question.questionId]}
                    onAnswerChange={handleAnswerChange}
                    validationErrors={validationErrors[question.questionId] || {}}
                    maxCharacters={maxCharacters}
                  />
                </Box>
              </div>
            );
          })}
        </Box>

        <Box className={classes.buttonGroup}>
          <Button
            id="tna-back-btn"
            type="button"
            variant="outlined"
            onClick={handleBackClick}
            className={classes.outlineButton}
            startIcon={<ArrowBackIos />}
          >
            {t("back")}
          </Button>
          {questions?.length > 0 && (
            <Button
              id="tna-next-btn"
              type="button"
              variant="contained"
              onClick={handleNext}
              className={classes.primaryButton}
              endIcon={<ArrowForwardIos />}
            >
              {t("find_courses")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
